// src/edu-world/data/session-saver.ts
// Auto-save on session end and emergency save on unexpected exit (beforeunload).
// On boot: read IndexedDB via persistence module, fallback to LocalStorage, then default.
// Preserves zone unlocks when recoverable from corrupted state.
// Requirements: 2.5, 2.6

import type { WorldState } from '../types/index';
import { saveWorldState, loadWorldState, getDefaultWorldState } from './persistence';

const EMERGENCY_LS_KEY = 'edu-world-emergency-state';

// ─── Emergency LocalStorage Save ───

/**
 * Synchronously saves world state to localStorage as an emergency fallback.
 * Called on `beforeunload` when IndexedDB async save may not complete.
 */
function emergencySaveToLocalStorage(state: WorldState): void {
  try {
    localStorage.setItem(EMERGENCY_LS_KEY, JSON.stringify(state));
  } catch {
    console.warn('[EduWorld] Emergency localStorage save failed');
  }
}

/**
 * Attempts to load emergency state from localStorage.
 * Returns null if not available or corrupted.
 */
function loadEmergencyFromLocalStorage(): WorldState | null {
  try {
    const raw = localStorage.getItem(EMERGENCY_LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (isValidWorldState(parsed)) {
      return parsed as WorldState;
    }
    return null;
  } catch {
    console.warn('[EduWorld] Emergency localStorage load failed');
    return null;
  }
}

/**
 * Clears emergency localStorage data after successful IndexedDB restore.
 */
function clearEmergencyLocalStorage(): void {
  try {
    localStorage.removeItem(EMERGENCY_LS_KEY);
  } catch {
    // Silently ignore — not critical
  }
}

// ─── Validation ───

function isValidWorldState(data: unknown): data is WorldState {
  if (!data || typeof data !== 'object') return false;
  const obj = data as Record<string, unknown>;

  if (typeof obj.missions !== 'object' || obj.missions === null) return false;
  if (!Array.isArray(obj.impacts_applied)) return false;
  if (
    !obj.player_position ||
    typeof (obj.player_position as Record<string, unknown>).x !== 'number' ||
    typeof (obj.player_position as Record<string, unknown>).y !== 'number'
  ) {
    return false;
  }
  if (typeof obj.current_zone !== 'string') return false;

  return true;
}

/**
 * Attempts to extract zone-related data from a partially corrupted state.
 * Returns zone unlock info if recoverable, null otherwise.
 */
function extractZoneUnlocks(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const obj = data as Record<string, unknown>;

  // Try to recover current_zone at minimum
  if (typeof obj.current_zone === 'string') {
    return obj.current_zone;
  }
  return null;
}

// ─── State tracking ───

let currentState: WorldState | null = null;
let beforeUnloadRegistered = false;

// ─── beforeunload handler ───

function handleBeforeUnload(): void {
  if (currentState) {
    emergencySaveToLocalStorage(currentState);
  }
}

// ─── Public API ───

/**
 * Registers the `beforeunload` listener for emergency saves.
 * Call this once during application initialization.
 * Updates the internal state reference whenever the world state changes.
 */
export function registerEmergencySave(getState: () => WorldState): void {
  currentState = getState();

  if (!beforeUnloadRegistered) {
    window.addEventListener('beforeunload', handleBeforeUnload);
    beforeUnloadRegistered = true;
  }
}

/**
 * Updates the internal state reference for emergency saves.
 * Call this whenever the world state changes so beforeunload saves the latest.
 */
export function updateEmergencyState(state: WorldState): void {
  currentState = state;
}

/**
 * Unregisters the `beforeunload` listener.
 * Call on controlled shutdown after full save completes.
 */
export function unregisterEmergencySave(): void {
  if (beforeUnloadRegistered) {
    window.removeEventListener('beforeunload', handleBeforeUnload);
    beforeUnloadRegistered = false;
  }
}

/**
 * Performs a full save on session end.
 * Calls `saveWorldState()` from the persistence module.
 * Also clears emergency localStorage since a full save succeeded.
 */
export async function saveOnSessionEnd(state: WorldState): Promise<void> {
  try {
    await saveWorldState(state);
    clearEmergencyLocalStorage();
  } catch {
    // Fallback: at least save to emergency localStorage
    console.warn('[EduWorld] Session end save failed, writing emergency fallback');
    emergencySaveToLocalStorage(state);
  }
}

/**
 * Loads world state on boot with full recovery chain:
 * 1. Try IndexedDB via `loadWorldState()`
 * 2. If corrupt/missing → try emergency localStorage
 * 3. If both fail → initialize default WorldState (preserve zone unlocks if recoverable)
 *
 * Never displays errors to the child.
 */
export async function loadOnBoot(): Promise<WorldState> {
  // Step 1: Try primary persistence (IndexedDB with its own localStorage fallback)
  try {
    const primary = await loadWorldState();
    if (primary) {
      clearEmergencyLocalStorage();
      return primary;
    }
  } catch {
    console.warn('[EduWorld] Primary load failed on boot');
  }

  // Step 2: Try emergency localStorage
  const emergency = loadEmergencyFromLocalStorage();
  if (emergency) {
    console.warn('[EduWorld] Recovered state from emergency localStorage');
    // Persist recovered state back to IndexedDB for future sessions
    try {
      await saveWorldState(emergency);
    } catch {
      // Non-critical — we have the state in memory
    }
    clearEmergencyLocalStorage();
    return emergency;
  }

  // Step 3: Both failed — initialize default, try to preserve zone unlocks
  console.warn('[EduWorld] All persistence sources failed, initializing default state');
  const defaultState = getDefaultWorldState();

  // Attempt partial recovery of zone info from any raw localStorage data
  try {
    const rawEmergency = localStorage.getItem(EMERGENCY_LS_KEY);
    if (rawEmergency) {
      const partial = JSON.parse(rawEmergency);
      const recoveredZone = extractZoneUnlocks(partial);
      if (recoveredZone) {
        defaultState.current_zone = recoveredZone;
      }
    }
  } catch {
    // Silently ignore — we already have a valid default
  }

  return defaultState;
}
